import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectOption } from '../select/select.component';

@Component({
    selector: 'lokos-multi-select',
    templateUrl: './multi-select.component.html',
    styleUrl: './multi-select.component.scss',
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectComponent),
            multi: true,
        },
    ],
})
export class MultiSelectComponent implements ControlValueAccessor {
    @Input() label = '';
    @Input() placeholder = 'select';
    @Input() options: SelectOption[] = [];
    @Input() disabled = false;
    @Input() max = 0;

    @Output() valueChange = new EventEmitter<string[]>();

    value: string[] = [];
    isOpen = false;

    private onChange: (value: string[]) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor(private elementRef: ElementRef) {}

    writeValue(value: string[] | null): void {
        this.value = Array.isArray(value) ? value.map((v) => `${v}`) : [];
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.isOpen = false;
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }

    togglePanel(): void {
        if (this.disabled) {
            return;
        }
        this.isOpen = !this.isOpen;
        if (!this.isOpen) {
            this.onTouched();
        }
    }

    isSelected(option: SelectOption): boolean {
        return this.value.includes(option.value);
    }

    isOptionDisabled(option: SelectOption): boolean {
        return this.max > 0 && this.value.length >= this.max && !this.isSelected(option);
    }

    toggleOption(option: SelectOption, event: Event): void {
        event.stopPropagation();
        if (this.disabled) {
            return;
        }
        if (this.isSelected(option)) {
            this.value = this.value.filter((v) => v !== option.value);
        } else {
            if (this.isOptionDisabled(option)) {
                return;
            }
            this.value = [...this.value, option.value];
        }
        this.emit();
    }

    removeChip(chipValue: string, event: Event): void {
        event.stopPropagation();
        if (this.disabled) {
            return;
        }
        this.value = this.value.filter((v) => v !== chipValue);
        this.emit();
    }

    chipLabel(chipValue: string): string {
        const match = this.options.find((o) => o.value === chipValue);
        return match ? match.label : chipValue;
    }

    private emit(): void {
        this.onChange(this.value);
        this.valueChange.emit(this.value);
        this.onTouched();
    }
}
